import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    message,
    Card,
    Avatar,
    Row,
    Col,
    Tag,
    Switch,
    Tooltip,
    Badge,
    Popconfirm,
    Empty,
    InputNumber,
    Popover
} from 'antd';
import {
    DollarOutlined,
    CrownOutlined,
    EyeOutlined,
    EditOutlined,
    ReloadOutlined,
    FilterOutlined,
    GiftOutlined,
    ToolOutlined,
    WifiOutlined
} from '@ant-design/icons';
import adminApi from '../config/adminApi';
import TextArea from 'antd/es/input/TextArea';
import moment from 'moment';

const { Option } = Select;
const { Search } = Input;

const Retailers = () => {
    const [retailers, setRetailers] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 15,
        total: 0
    });
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRetailer, setEditingRetailer] = useState(null);
    const [form] = Form.useForm();
    const [packages, setPackages] = useState([]);
    const [businessTypes, setBusinessTypes] = useState([]);
    const [filterValues, setFilterValues] = useState({
        is_paid: null,
        is_trial: null,
        status: null,
        search: ''
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [paymentModal, setPaymentModal] = useState({
        visible: false,
        payments: []
    });


    useEffect(() => {
        fetchRetailers();
        fetchPackages();
        fetchBusinessTypes();
    }, []);

    const fetchRetailers = async (page = 1, pageSize = 15) => {
        setLoading(true);
        try {
            const params = {
                page,
                per_page: pageSize,
                ...filterValues
            };

            // Remove null or empty values from params
            Object.keys(params).forEach(key => {
                if (params[key] === null || params[key] === '') {
                    delete params[key];
                }
            });

            const response = await adminApi.get('/admin/retailers', { params });

            if (response.data.success) {
                setRetailers(response.data.data.data);
                setPagination({
                    current: response.data.data.current_page,
                    pageSize: response.data.data.per_page,
                    total: response.data.data.total
                });
            } else {
                message.error(response.data.message || 'Failed to fetch retailers');
                setRetailers([]);
            }
        } catch (error) {
            console.error('Failed to fetch retailers:', error);
            message.error('Failed to fetch retailers.');
            setRetailers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPackages = async () => {
        try {
            const response = await adminApi.get('/packages');
            setPackages(response.data.packages || []);
        } catch (error) {
            console.error('Failed to fetch packages:', error);
        }
    };

    const fetchBusinessTypes = async () => {
        try {
            const response = await adminApi.get('/business-types');
            setBusinessTypes(response.data.business_types || []);
        } catch (error) {
            console.error('Failed to fetch business types:', error);
        }
    };

    const handleTableChange = (pagination, filters, sorter) => {
        fetchRetailers(pagination.current, pagination.pageSize);
    };

    const handleFilterChange = (key, value) => {
        setFilterValues(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        fetchRetailers(1, pagination.pageSize);
    };

    const showEditModal = (retailer = null) => {
        setEditingRetailer(retailer);
        if (retailer) {
            form.setFieldsValue({
                is_paid: retailer.is_paid,
                is_trial: retailer.is_trial,
                package_id: retailer.package_id
            });
        }
        setIsModalVisible(true);
    };

    const handleUpdate = async () => {
        try {
            const values = await form.validateFields();

            // Validate amount is required when is_paid is true
            if (values.is_paid && !values.amount) {
                message.error('Amount is required when marking as paid');
                return;
            }

            setLoading(true);

            // Prepare update data
            const updateData = {
                is_paid: values.is_paid,
                is_trial: values.is_trial,
                package_id: values.package_id,
                amount: values.amount || 0,
                notes: values.notes || ''
            };

            await adminApi.patch(`/admin/retailers/${editingRetailer.id}/payment-status`, updateData);
            message.success('Retailer updated successfully');

            setIsModalVisible(false);
            form.resetFields();
            fetchRetailers(pagination.current, pagination.pageSize);
        } catch (error) {
            console.error('Error updating retailer:', error);
            message.error('Failed to update retailer.');
        } finally {
            setLoading(false);
        }
    };

    const assignTechnicalSupport = async (retailerId) => {
        try {
            await adminApi.post(
                `/admin/retailers/${retailerId}/assign-technical-support`
            );

            message.success('Technical support assigned successfully');
        } catch (error) {
            console.error(error);
            message.error('Failed to assign technical support');
        }
    };

    const techSupportActions = (record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Assign Technical Support */}
            <Popconfirm
                title="Assign technical support to all shops?"
                onConfirm={() => assignTechnicalSupport(record.id)}
                okText="Yes"
                cancelText="No"
            >
                <Button type="text" icon={<ToolOutlined />}>
                    Assign Technical Support
                </Button>
            </Popconfirm>

            {/* Reset Password */}
            <Popconfirm
                title="Reset technical support password?"
                onConfirm={async () => {
                    try {
                        const response = await adminApi.post(
                            `/admin/retailers/${record.id}/reset-technical-support-password`
                        );

                        if (response.data.success) {
                            message.success(
                                `Password reset successfully for ${response.data.technical_email}`
                            );
                        } else {
                            message.error(response.data.message || 'Failed to reset password');
                        }
                    } catch (error) {
                        console.error(error);
                        message.error('Failed to reset password');
                    }
                }}
                okText="Reset"
                cancelText="Cancel"
            >
                <Button type="text" danger icon={<ReloadOutlined />}>
                    Reset Password
                </Button>
            </Popconfirm>

            {/* Offline Access Toggle — NEW */}
            <Popconfirm
                title={record.offline_enabled
                    ? 'Disable offline access for this retailer?'
                    : 'Enable offline access for this retailer?'}
                onConfirm={() => toggleOfflineAccess(record.id, record.offline_enabled)}
                okText="Yes"
                cancelText="No"
            >
                <Button
                    type="text"
                    icon={<WifiOutlined />}
                    danger={record.offline_enabled}
                >
                    {record.offline_enabled ? 'Disable Offline Access' : 'Enable Offline Access'}
                </Button>
            </Popconfirm>
        </div>
    );

    const toggleOfflineAccess = async (retailerId, currentlyEnabled) => {
        try {
            await adminApi.patch(`/admin/retailers/${retailerId}/offline-access`, {
                offline_enabled: !currentlyEnabled
            });
            message.success(
                !currentlyEnabled
                    ? 'Offline access enabled'
                    : 'Offline access disabled'
            );
            fetchRetailers(pagination.current, pagination.pageSize);
        } catch (error) {
            console.error(error);
            message.error('Failed to update offline access');
        }
    };


    const handleBulkUpdate = async (field, value) => {
        try {
            const selectedIds = selectedRowKeys;
            if (selectedIds.length === 0) {
                message.warning('Please select at least one retailer');
                return;
            }

            const updateData = { retailer_ids: selectedIds, [field]: value };

            // Ensure logical consistency
            if (field === 'is_paid' && value === true) {
                updateData.is_trial = false;
            } else if (field === 'is_trial' && value === true) {
                updateData.is_paid = false;
            }

            await adminApi.patch('/admin/retailers/bulk-payment-status', updateData);
            message.success(`Updated ${selectedIds.length} retailers successfully`);

            setSelectedRowKeys([]);
            fetchRetailers(pagination.current, pagination.pageSize);
        } catch (error) {
            console.error('Error in bulk update:', error);
            message.error('Failed to update retailers.');
        }
    };

    const handleBulkPackageUpdate = async (packageId) => {
        try {
            const selectedIds = selectedRowKeys;
            if (selectedIds.length === 0) {
                message.warning('Please select at least one retailer');
                return;
            }

            if (!packageId) {
                message.warning('Please select a package');
                return;
            }

            await adminApi.patch('/admin/retailers/bulk-package-update', {
                retailer_ids: selectedIds,
                package_id: packageId
            });

            message.success(`Package updated for ${selectedIds.length} retailers successfully`);
            setSelectedRowKeys([]);
            fetchRetailers(pagination.current, pagination.pageSize);
        } catch (error) {
            console.error('Error in bulk package update:', error);
            message.error('Failed to update packages.');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingRetailer(null);
    };

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const columns = [
        {
            title: 'Retailer',
            key: 'retailer',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        src={record.image_logo || 'https://via.placeholder.com/40'}
                        size={40}
                        style={{ marginRight: 10 }}
                    />
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{record.business_name}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{record.full_name}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Contact',
            key: 'contact',
            render: (_, record) => (
                <div>
                    <div>{record.email}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{record.phone}</div>
                </div>
            ),
            responsive: ['md'],
        },
        {
            title: 'Package',
            key: 'package',
            render: (_, record) => (
                <Tag color="blue">{record.package?.name || 'N/A'}</Tag>
            ),
            responsive: ['lg'],
        },
        {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            render: (status) => {
                const isActive = Boolean(status);
                return (
                    <Tag color={isActive ? 'green' : 'red'}>
                        {isActive ? 'ACTIVE' : 'INACTIVE'}
                    </Tag>
                );
            },
        },
        {
            title: 'Payment',
            key: 'payment',
            render: (_, record) => (
                <div>
                    <div>
                        <Badge
                            status={record.is_paid ? 'success' : 'default'}
                            text={record.is_paid ? 'Paid' : 'Unpaid'}
                        />
                    </div>
                    <div>
                        <Badge
                            status={record.is_trial ? 'processing' : 'default'}
                            text={record.is_trial ? 'Trial' : 'Regular'}
                        />
                    </div>
                </div>
            ),
        },
        {
            title: 'Shops/Warehouses',
            key: 'locations',
            render: (_, record) => (
                <div>
                    <div>Shops: {record.shops?.length || 0}</div>
                    <div>Warehouses: {record.warehouses?.length || 0}</div>
                </div>
            ),
            responsive: ['xl'],
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <div>
                    <Tooltip title="View Payments">
                        <Button
                            icon={<DollarOutlined />}
                            style={{ marginRight: 8 }}
                            onClick={() => {
                                setPaymentModal({
                                    visible: true,
                                    payments: record.payments || []
                                });
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="View Details">
                        <Button
                            icon={<EyeOutlined />}
                            style={{ marginRight: 8 }}
                            onClick={() => message.info('View details feature to be implemented')}
                        />
                    </Tooltip>
                    <Tooltip title="Edit Payment Status & Package">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => showEditModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Technical Support Actions">
                        <Popover
                            content={techSupportActions(record)}
                            trigger="click"
                        >
                            <Button
                                icon={<ToolOutlined />}
                                style={{ marginRight: 8 }}
                            />
                        </Popover>
                    </Tooltip>


                </div>
            ),
        },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Card
                title="Retailer Management"
                extra={
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => fetchRetailers(pagination.current, pagination.pageSize)}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                }
            >
                {/* Filters Section */}
                <Card
                    style={{ marginBottom: 20 }}
                    title={
                        <span>
                            <FilterOutlined style={{ marginRight: 8 }} />
                            Filters
                        </span>
                    }
                    size="small"
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Search
                                placeholder="Search retailers..."
                                onSearch={applyFilters}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Select
                                placeholder="Payment Status"
                                style={{ width: '100%' }}
                                allowClear
                                onChange={(value) => handleFilterChange('is_paid', value)}
                                onBlur={applyFilters}
                            >
                                <Option value={true}>Paid</Option>
                                <Option value={false}>Unpaid</Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Select
                                placeholder="Trial Status"
                                style={{ width: '100%' }}
                                allowClear
                                onChange={(value) => handleFilterChange('is_trial', value)}
                                onBlur={applyFilters}
                            >
                                <Option value={true}>Trial</Option>
                                <Option value={false}>Regular</Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Select
                                placeholder="Account Status"
                                style={{ width: '100%' }}
                                allowClear
                                onChange={(value) => handleFilterChange('status', value)}
                                onBlur={applyFilters}
                            >
                                <Option value={true}>Active</Option>
                                <Option value={false}>Inactive</Option>
                            </Select>
                        </Col>
                        <Col xs={24}>
                            <Button type="primary" onClick={applyFilters}>
                                Apply Filters
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {/* Bulk Actions */}
                {selectedRowKeys.length > 0 && (
                    <Card style={{ marginBottom: 20 }} size="small">
                        <span style={{ marginRight: 16 }}>
                            Selected {selectedRowKeys.length} retailers
                        </span>
                        <Popconfirm
                            title="Mark selected as paid?"
                            onConfirm={() => handleBulkUpdate('is_paid', true)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                icon={<DollarOutlined />}
                                type="primary"
                                ghost
                                style={{ marginRight: 8 }}
                            >
                                Mark as Paid
                            </Button>
                        </Popconfirm>
                        <Popconfirm
                            title="Mark selected as trial?"
                            onConfirm={() => handleBulkUpdate('is_trial', true)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                icon={<CrownOutlined />}
                                type="primary"
                                ghost
                                style={{ marginRight: 8 }}
                            >
                                Mark as Trial
                            </Button>
                        </Popconfirm>
                        <Popconfirm
                            title="Update package for selected retailers?"
                            onConfirm={() => {
                                const selectedPackage = prompt('Enter package ID:');
                                if (selectedPackage) {
                                    handleBulkPackageUpdate(parseInt(selectedPackage));
                                }
                            }}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                icon={<GiftOutlined />}
                                type="primary"
                                ghost
                                style={{ marginRight: 8 }}
                            >
                                Update Package
                            </Button>
                        </Popconfirm>
                        <Button onClick={() => setSelectedRowKeys([])}>
                            Clear Selection
                        </Button>
                    </Card>
                )}

                {/* Retailers Table */}
                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={retailers}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: <Empty description="No retailers found" />
                    }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} retailers`
                    }}
                    onChange={handleTableChange}
                />

                {/* Edit Modal */}
                {/* Edit Modal */}
                <Modal
                    title={`Edit Retailer - ${editingRetailer?.business_name}`}
                    visible={isModalVisible}
                    onOk={handleUpdate}
                    onCancel={handleCancel}
                    okText="Update"
                    confirmLoading={loading}
                    width={600}
                >
                    <Form
                        form={form}
                        layout="vertical"
                    >
                        <Form.Item
                            label="Package"
                            name="package_id"
                            rules={[{ required: true, message: 'Please select a package' }]}
                        >
                            <Select placeholder="Select Package">
                                {packages.map(pkg => (
                                    <Option key={pkg.id} value={pkg.id}>
                                        {pkg.name} - TZS{pkg.price} ({pkg.duration_days} days)
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Paid Status"
                            name="is_paid"
                            valuePropName="checked"
                        >
                            <Switch
                                checkedChildren="Paid"
                                unCheckedChildren="Unpaid"
                                onChange={(checked) => {
                                    if (checked) {
                                        form.setFieldsValue({ is_trial: false });
                                    } else {
                                        form.setFieldsValue({ amount: null, notes: '' });
                                    }
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.is_paid !== currentValues.is_paid}
                        >
                            {({ getFieldValue }) =>
                                getFieldValue('is_paid') ? (
                                    <>
                                        <Form.Item
                                            label="Payment Amount"
                                            name="amount"
                                            rules={[
                                                { required: true, message: 'Please enter payment amount' },
                                                { type: 'number', min: 0.01, message: 'Amount must be greater than 0' }
                                            ]}
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                prefix="Tsh"
                                                placeholder="Enter amount paid"
                                                min={0.01}
                                                step={0.01}
                                                precision={2}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            label="Notes (Optional)"
                                            name="notes"
                                        >
                                            <TextArea
                                                rows={3}
                                                placeholder="Add any notes about this payment"
                                            />
                                        </Form.Item>
                                    </>
                                ) : null
                            }
                        </Form.Item>

                        <Form.Item
                            label="Trial Status"
                            name="is_trial"
                            valuePropName="checked"
                        >
                            <Switch
                                checkedChildren="Trial"
                                unCheckedChildren="Regular"
                                onChange={(checked) => {
                                    if (checked) {
                                        form.setFieldsValue({ is_paid: false, amount: null, notes: '' });
                                    }
                                }}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
                <Modal
                    title="Latest 10 Payments"
                    open={paymentModal.visible}
                    onCancel={() => setPaymentModal({ visible: false, payments: [] })}
                    footer={null}
                    width={600}
                >
                    {paymentModal.payments.length === 0 ? (
                        <Empty description="No payments found" />
                    ) : (
                        <Table
                            dataSource={paymentModal.payments}
                            rowKey={(record, index) => index}
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: 'Amount',
                                    dataIndex: 'amount',
                                    render: (value) => `Tsh ${value}`,
                                },
                                {
                                    title: 'Method',
                                    dataIndex: 'payment_method',
                                    render: (value) => value || 'N/A',
                                },
                                {
                                    title: 'Date',
                                    dataIndex: 'payment_date',
                                    render: (date) => date ? moment(date).format('DD MMM YYYY HH:mm') : '-',
                                },
                                {
                                    title: 'Notes',
                                    dataIndex: 'notes',
                                    render: (value) => value || '-',
                                },
                            ]}
                        />
                    )}
                </Modal>
            </Card>
        </div>
    );
};

export default Retailers;